<template>
  <!-- Desktop menu of the app screens (/app/*). Hidden below 1024px, where the
       screens keep their own mobile navigation. -->
  <aside id="navigation-side-app">
    <div class="brand">
      <div class="brand-mark">
        <font-awesome-icon :icon="['fas', 'car']" />
      </div>
      <div>
        <div class="brand-name">FACILAUTO</div>
        <div class="brand-sub">{{ app.strings.app.side.subtitle }}</div>
      </div>
    </div>

    <div class="user">
      <div class="avatar">{{ initials }}</div>
      <div>
        <div class="user-name" :title="app.user.name">{{ app.user.name }}</div>
        <div class="user-role">{{ isTeacher ? app.strings.app.side.teacher : app.strings.app.side.student }}</div>
      </div>
    </div>

    <nav>
      <a v-for="item in items" :key="item.key" class="item" :class="{active: item.active}" v-on:click="go(item)">
        <span class="icon"><font-awesome-icon :icon="item.icon" /></span>
        <span>{{ item.label }}</span>
        <span class="badge" v-if="item.badge > 0">{{ item.badge }}</span>
      </a>
    </nav>

    <div class="filler"></div>

    <a class="item exit" v-on:click="exit()">
      <span class="icon"><font-awesome-icon :icon="isTeacher ? ['fas', 'power-off'] : ['fas', 'chevron-left']" /></span>
      <span>{{ isTeacher ? app.strings.app.side.logout : app.strings.app.side.back }}</span>
    </a>
  </aside>
</template>

<script>
export default {
  name: 'navigation-side-app-component',
  props: [
    'app'
  ],
  computed: {
    isTeacher () {
      return this.app.user.role === 'teacher'
    },
    initials () {
      let parts = (this.app.user.name || '').trim().split(/\s+/).filter((p) => p.length > 0)
      return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('')
    },
    items () {
      let name = this.$route.name
      let student = this.$route.query.student
      let s = this.app.strings.app.side
      if (this.isTeacher) {
        return [
          {key: 'home', label: s.home, icon: ['fas', 'home'], route: {name: 'app.teacher.home'}, active: name === 'app.teacher.home'},
          {key: 'calendar', label: s.calendar, icon: ['far', 'calendar-alt'], route: {name: 'app.practice.calendar'}, active: name === 'app.practice.calendar' || (name || '').indexOf('app.teacher.practice') === 0},
          {key: 'students', label: s.students, icon: ['fas', 'list'], route: {name: 'app.student.list'}, active: name === 'app.student.list' || ((name || '').indexOf('app.student') === 0 && name !== 'app.student.list')},
          {key: 'biometric', label: s.biometric, icon: ['fas', 'fingerprint'], route: {name: 'app.teacher.biometric.courses'}, active: (name || '').indexOf('app.teacher.biometric') === 0},
          {key: 'help', label: s.help, icon: ['fas', 'question-circle'], route: {name: 'app.help'}, active: name === 'app.help'}
        ]
      }
      let code = this.app.user.code
      return [
        {key: 'profile', label: s.profile, icon: ['fas', 'user'], route: {name: 'app.student.profile', params: {code: code}}, active: name === 'app.student.profile'},
        {key: 'calendar', label: s.my_classes, icon: ['far', 'calendar-alt'], route: {name: 'app.practice.calendar', query: {student: code}}, active: name === 'app.practice.calendar' && student !== undefined},
        {key: 'evolution', label: s.evolution, icon: ['fas', 'chart-pie'], route: {name: 'app.student.evolution', params: {code: code}}, active: name === 'app.student.evolution'},
        {key: 'dgt', label: s.dgt, icon: ['far', 'file'], route: {name: 'app.student.dgtreport', params: {code: code}}, active: name === 'app.student.dgtreport'},
        {key: 'bag', label: s.bag, icon: ['fas', 'shopping-bag'], route: {name: 'app.student.practice.bag.list'}, active: (name || '').indexOf('app.student.practice.bag') === 0}
      ].concat(this.app.studentBooking && this.app.studentBooking !== 'off'
        ? [{key: 'book', label: s.book, icon: ['far', 'calendar-plus'], route: {name: 'app.student.practice.book'}, active: name === 'app.student.practice.book', badge: this.app.studentBookingNotices || 0}]
        : [])
        .concat([{key: 'help', label: s.help, icon: ['fas', 'question-circle'], route: {name: 'app.help'}, active: name === 'app.help'}])
    }
  },
  mounted () {
    this.loadStudentBooking(this.app)
  },
  watch: {
    // The session user may arrive after the menu is mounted; and the notices
    // change after the student looks at them.
    'app.user.role' () {
      this.loadStudentBooking(this.app)
    },
    '$route.name' () {
      this.loadStudentBooking(this.app)
    }
  },
  methods: {
    go (item) {
      let query = Object.assign({origin: 'app', navigationLeft: 'false', token: this.app.token}, item.route.query || {})
      this.$router.push({name: item.route.name, params: item.route.params || {}, query: query}).catch(() => {})
    },
    exit () {
      if (this.isTeacher) {
        this.app.methods.logout()
      } else {
        this.app.methods.back()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#navigation-side-app {
  display: none;
}

@media (min-width: 1024px) {
  #navigation-side-app {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 1100;
    display: flex;
    flex-direction: column;
    width: 264px;
    padding: 22px 16px;
    box-sizing: border-box;
    background-color: #FFFFFF;
    border-right: 1px solid #e8edf3;
    font-family: 'Roboto', sans-serif;
  }
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 6px 22px;
}

.brand-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: #2071b7;
  color: #FFFFFF;
  font-size: 18px;
  transform: rotate(12deg);
}

.brand-mark > svg {
  transform: rotate(-12deg);
}

.brand-name {
  color: #0f172a;
  font-family: 'Lexend', sans-serif;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.brand-sub {
  color: #64748b;
  font-size: 12px;
}

.user {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  padding: 12px;
  border-radius: 14px;
  background-color: #f5f8fb;
}

.user > div:nth-of-type(2) {
  min-width: 0;
}

.avatar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background-color: rgba(32, 113, 183, 0.12);
  color: #2071b7;
  font-family: 'Lexend', sans-serif;
  font-weight: 700;
}

.user-name {
  overflow: hidden;
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.item .badge {
  margin-left: auto;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background-color: #dc3545;
  color: #FFFFFF;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  box-sizing: border-box;
}

.user-role {
  color: #64748b;
  font-size: 12px;
}

nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  color: #334155;
  font-family: 'Lexend', sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.item .icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background-color: #f5f8fb;
  color: #64748b;
}

.item:hover {
  background-color: #f5f8fb;
}

.item.active {
  background-color: rgba(32, 113, 183, 0.1);
  color: #2071b7;
}

.item.active .icon {
  background-color: #2071b7;
  color: #FFFFFF;
}

.filler {
  flex: 1;
}

.item.exit {
  color: #cf225f;
}

.item.exit .icon {
  background-color: rgba(207, 34, 95, 0.1);
  color: #cf225f;
}
</style>
